import { useCallback } from 'react';
import { usePartnerDataClient } from '../api/use-partner-data-client';
import { IngestionSummaryStats } from '../domain/types';
import { IngestionStats } from '../types';
import {
  computeIngestionStats,
  EMPTY_INGESTION_STATS,
} from '../utils/ingestion-summary.utils';
import { useAsyncResource } from './use-async-resource';

const DEFAULT_WINDOW_HOURS = 24;

const EMPTY_SUMMARY: IngestionSummaryStats = {
  byStatus: {},
  totalRowsParsed: '0',
  window: { hours: DEFAULT_WINDOW_HOURS, since: '' },
};

export interface UseIngestionSummaryReturn {
  stats: IngestionStats;
  isLoading: boolean;
  hasError: boolean;
  refetch: () => void;
}

/**
 * Fetches the ingestion summary for a rolling window and reduces the opaque
 * `byStatus` map into the aggregated counts the KPI band renders.
 */
export const useIngestionSummary = (
  windowHours: number = DEFAULT_WINDOW_HOURS,
): UseIngestionSummaryReturn => {
  const client = usePartnerDataClient();

  const fetchSummary = useCallback(
    () => client.getSummary(windowHours),
    [client, windowHours],
  );

  const { data, isLoading, hasError, refetch } = useAsyncResource(
    fetchSummary,
    EMPTY_SUMMARY,
  );

  const stats = hasError
    ? EMPTY_INGESTION_STATS
    : computeIngestionStats(data.byStatus, data.totalRowsParsed);

  return { stats, isLoading, hasError, refetch };
};
